import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pencil,
  Trash2,
  Code2,
  ChevronRight,
  ChevronDown,
  List,
  GitBranch,
  AlertTriangle,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { SearchInput } from '@/components/shared/SearchInput';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { supabase } from '@/integrations/supabase/client';
import { generateSlug } from '@/lib/utils';
import { cn } from '@/lib/utils';

// Types
interface AdfElementItem {
  id: string;
  slug: string;
  tag_name: string;
  display_name: string;
  description: string | null;
  attributes: Record<string, unknown> | null;
  is_required: boolean | null;
  is_repeatable: boolean | null;
  is_active: boolean | null;
  parent_element_id: string | null;
  example_value: string | null;
  display_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

interface FormData {
  slug: string;
  tag_name: string;
  display_name: string;
  description: string;
  attributes: string;
  is_required: boolean;
  is_repeatable: boolean;
  is_active: boolean;
  parent_element_id: string;
  example_value: string;
  display_order: number;
}

interface TreeNode extends AdfElementItem {
  children: TreeNode[];
  depth: number;
}

// Helper to build tree structure
function buildElementTree(elements: AdfElementItem[]): TreeNode[] {
  const elementMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  // First pass: create nodes
  elements.forEach((el) => {
    elementMap.set(el.id, { ...el, children: [], depth: 0 });
  });

  // Second pass: build tree
  elements.forEach((el) => {
    const node = elementMap.get(el.id)!;
    if (el.parent_element_id && elementMap.has(el.parent_element_id)) {
      const parent = elementMap.get(el.parent_element_id)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort children by display_order
  const sortChildren = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    nodes.forEach((n) => sortChildren(n.children));
  };
  sortChildren(roots);

  return roots;
}

// Helper to get all descendant IDs
function getDescendantIds(element: TreeNode): string[] {
  const ids: string[] = [];
  const traverse = (node: TreeNode) => {
    ids.push(node.id);
    node.children.forEach(traverse);
  };
  element.children.forEach(traverse);
  return ids;
}

// Tree node component for tree view
function TreeNodeRow({
  node,
  parentMap,
  childCounts,
  onEdit,
  onDelete,
  expandedNodes,
  toggleExpanded,
}: {
  node: TreeNode;
  parentMap: Map<string, AdfElementItem>;
  childCounts: Record<string, number>;
  onEdit: (item: AdfElementItem) => void;
  onDelete: (item: AdfElementItem) => void;
  expandedNodes: Set<string>;
  toggleExpanded: (id: string) => void;
}) {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children.length > 0;

  return (
    <div 
      className={cn(
        node.depth > 0 && 'ml-4 pl-3 border-l-2 border-muted'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2 py-1.5 px-2 hover:bg-muted/50 rounded-md cursor-pointer group',
          'transition-colors'
        )}
        onClick={() => onEdit(node)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(node.id);
            }}
            className="p-0.5 hover:bg-muted rounded shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono shrink-0">
          &lt;{node.tag_name}&gt;
        </code>

        <span className="text-sm font-medium flex-1 truncate">{node.display_name}</span>

        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {node.is_required && (
            <Badge variant="outline" className="text-xs h-5">
              Required
            </Badge>
          )}
          {node.is_repeatable && (
            <Badge variant="secondary" className="text-xs h-5">
              Repeatable
            </Badge>
          )}
          {childCounts[node.id] > 0 && (
            <Badge variant="secondary" className="text-xs h-5">
              {childCounts[node.id]} children
            </Badge>
          )}
        </div>

        <div
          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(node)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(node)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-0.5">
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              parentMap={parentMap}
              childCounts={childCounts}
              onEdit={onEdit}
              onDelete={onDelete}
              expandedNodes={expandedNodes}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdfElementsManager() {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');
  const [childCounts, setChildCounts] = useState<Record<string, number>>({});
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const {
    items: elements,
    loading,
    editorOpen,
    setEditorOpen,
    editingItem,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    isDeleting,
    fetchData,
    openCreateEditor,
    openEditEditor,
    handleSave,
    confirmDelete,
    handleDelete,
  } = useAdminCrud<AdfElementItem, FormData>({
    table: 'adf_elements',
    select: '*',
    orderBy: { column: 'display_order' },
    entityName: 'element',
    getInitialFormData: () => ({
      slug: '',
      tag_name: '',
      display_name: '',
      description: '',
      attributes: '{}',
      is_required: false,
      is_repeatable: false,
      is_active: true,
      parent_element_id: '',
      example_value: '',
      display_order: 0,
    }),
    mapItemToForm: (item) => ({
      slug: item.slug,
      tag_name: item.tag_name,
      display_name: item.display_name,
      description: item.description || '',
      attributes: item.attributes ? JSON.stringify(item.attributes, null, 2) : '{}',
      is_required: item.is_required ?? false,
      is_repeatable: item.is_repeatable ?? false,
      is_active: item.is_active ?? true,
      parent_element_id: item.parent_element_id || '',
      example_value: item.example_value || '',
      display_order: item.display_order || 0,
    }),
    buildPayload: (form) => {
      let parsedAttrs = null;
      try {
        const parsed = JSON.parse(form.attributes);
        if (Object.keys(parsed).length > 0) {
          parsedAttrs = parsed;
        }
      } catch {
        // Invalid JSON, will be caught by validation
      }

      return {
        slug: form.slug || generateSlug(form.display_name),
        tag_name: form.tag_name,
        display_name: form.display_name,
        description: form.description || null,
        attributes: parsedAttrs,
        is_required: form.is_required,
        is_repeatable: form.is_repeatable,
        is_active: form.is_active,
        parent_element_id: form.parent_element_id || null,
        example_value: form.example_value || null,
        display_order: form.display_order,
      };
    },
    validatePayload: (form) => {
      if (!form.tag_name.trim()) return 'Tag name is required';
      if (!form.display_name.trim()) return 'Display name is required';
      if (form.attributes) {
        try {
          JSON.parse(form.attributes);
        } catch {
          return 'Attributes must be valid JSON';
        }
      }
      return null;
    },
  });

  // Fetch child counts
  useEffect(() => {
    if (elements.length === 0) return;

    const counts: Record<string, number> = {};
    elements.forEach((el) => {
      if (el.parent_element_id) {
        counts[el.parent_element_id] = (counts[el.parent_element_id] || 0) + 1;
      }
    });
    setChildCounts(counts);
  }, [elements]);

  // Build parent map for lookups
  const parentMap = useMemo(() => {
    const map = new Map<string, AdfElementItem>();
    elements.forEach((el) => map.set(el.id, el));
    return map;
  }, [elements]);

  // Build tree for tree view
  const elementTree = useMemo(() => buildElementTree(elements), [elements]);

  // Get parent options (exclude self and descendants when editing)
  const parentOptions = useMemo(() => {
    if (!editingItem) return elements.filter((el) => el.is_active);

    // Get all descendant IDs to exclude
    const tree = buildElementTree(elements);
    const findNode = (nodes: TreeNode[], id: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        const found = findNode(node.children, id);
        if (found) return found;
      }
      return null;
    };

    const currentNode = findNode(tree, editingItem.id);
    const excludeIds = new Set<string>([editingItem.id]);
    if (currentNode) {
      getDescendantIds(currentNode).forEach((id) => excludeIds.add(id));
    }

    return elements.filter((el) => !excludeIds.has(el.id) && el.is_active);
  }, [elements, editingItem]);

  // Filter elements by search
  const filteredElements = useMemo(() => {
    if (!searchQuery) return elements;
    const q = searchQuery.toLowerCase();
    return elements.filter(
      (el) =>
        el.tag_name.toLowerCase().includes(q) ||
        el.display_name.toLowerCase().includes(q) ||
        el.slug.toLowerCase().includes(q)
    );
  }, [elements, searchQuery]);

  // Filter tree by search
  const filteredTree = useMemo(() => {
    if (!searchQuery) return elementTree;
    // For simplicity, just show matching elements flat in tree view when searching
    return buildElementTree(filteredElements);
  }, [elementTree, searchQuery, filteredElements]);

  // Toggle tree node expansion
  const toggleExpanded = (id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Expand all nodes
  const expandAll = () => {
    const allIds = new Set<string>();
    const addIds = (nodes: TreeNode[]) => {
      nodes.forEach((n) => {
        if (n.children.length > 0) {
          allIds.add(n.id);
          addIds(n.children);
        }
      });
    };
    addIds(elementTree);
    setExpandedNodes(allIds);
  };

  // Get parent name for display
  const getParentName = (parentId: string | null) => {
    if (!parentId) return null;
    const parent = parentMap.get(parentId);
    return parent?.display_name || null;
  };

  // Get delete warning if element has children
  const deleteWarning =
    itemToDelete && childCounts[itemToDelete.id]
      ? `This element has ${childCounts[itemToDelete.id]} child element(s). Deleting it will remove the parent reference from those children.`
      : undefined;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="ADF Elements"
        count={elements.length}
        onAdd={openCreateEditor}
        addLabel="Add Element"
      />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by tag, name, or slug..."
          className="w-full sm:w-72"
        />

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            <List className="h-4 w-4 mr-1.5" />
            Table
          </Button>
          <Button
            variant={viewMode === 'tree' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('tree')}
          >
            <GitBranch className="h-4 w-4 mr-1.5" />
            Tree
          </Button>
          {viewMode === 'tree' && (
            <Button variant="outline" size="sm" onClick={expandAll}>
              Expand All
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <AdminTableSkeleton columns={7} rows={8} />
      ) : elements.length === 0 ? (
        <AdminEmptyState
          icon={Code2}
          title="No ADF elements yet"
          description="Create ADF elements to define the XML structure."
          actionLabel="Add Element"
          onAction={openCreateEditor}
        />
      ) : viewMode === 'table' ? (
        // Table View
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead className="w-20">Required</TableHead>
                <TableHead className="w-24">Repeatable</TableHead>
                <TableHead className="w-20">Active</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredElements.map((element) => (
                <TableRow
                  key={element.id}
                  className="cursor-pointer"
                  onClick={() => openEditEditor(element)}
                >
                  <TableCell>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                      &lt;{element.tag_name}&gt;
                    </code>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{element.display_name}</div>
                    {element.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {element.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {element.parent_element_id ? (
                      <Badge variant="outline" className="font-mono text-xs">
                        {getParentName(element.parent_element_id) || 'Unknown'}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Root
                      </Badge>
                    )}
                    {childCounts[element.id] > 0 && (
                      <Badge variant="outline" className="ml-1 text-xs">
                        {childCounts[element.id]} children
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={element.is_required ? 'default' : 'outline'}>
                      {element.is_required ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={element.is_repeatable ? 'secondary' : 'outline'}>
                      {element.is_repeatable ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={element.is_active ? 'default' : 'outline'}>
                      {element.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditEditor(element)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => confirmDelete(element)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        // Tree View
        <div className="border rounded-lg p-3 bg-card">
          {filteredTree.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">
              No elements match your search.
            </p>
          ) : (
            filteredTree.map((node) => (
              <TreeNodeRow
                key={node.id}
                node={node}
                parentMap={parentMap}
                childCounts={childCounts}
                onEdit={openEditEditor}
                onDelete={confirmDelete}
                expandedNodes={expandedNodes}
                toggleExpanded={toggleExpanded}
              />
            ))
          )}
        </div>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingItem ? 'Edit Element' : 'Add Element'}
        description={
          editingItem
            ? 'Update the ADF element details.'
            : 'Create a new ADF element for the XML structure.'
        }
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingItem ? 'Update' : 'Create'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tag_name">Tag Name *</Label>
              <Input
                id="tag_name"
                value={formData.tag_name}
                onChange={(e) => setFormData({ ...formData, tag_name: e.target.value })}
                placeholder="e.g., prospect"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">The XML tag name</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Prospect"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="auto-generated if empty"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="parent_element_id">Parent Element</Label>
              <Select
                value={formData.parent_element_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, parent_element_id: value === 'none' ? '' : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent (or leave as root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">None (Root Element)</span>
                  </SelectItem>
                  {parentOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      <span className="font-mono text-xs mr-2">&lt;{opt.tag_name}&gt;</span>
                      {opt.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this element..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="example_value">Example Value</Label>
            <Input
              id="example_value"
              value={formData.example_value}
              onChange={(e) => setFormData({ ...formData, example_value: e.target.value })}
              placeholder="e.g., John Smith"
            />
            <p className="text-xs text-muted-foreground">
              Sample content to use in generated examples
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attributes">Attributes (JSON)</Label>
            <Textarea
              id="attributes"
              value={formData.attributes}
              onChange={(e) => setFormData({ ...formData, attributes: e.target.value })}
              placeholder='{"status": "new"}'
              rows={3}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              JSON object defining element attributes
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_required">Required</Label>
                <p className="text-xs text-muted-foreground">This element must be included</p>
              </div>
              <Switch
                id="is_required"
                checked={formData.is_required}
                onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_repeatable">Repeatable</Label>
                <p className="text-xs text-muted-foreground">This element can appear multiple times</p>
              </div>
              <Switch
                id="is_repeatable"
                checked={formData.is_repeatable}
                onCheckedChange={(checked) => setFormData({ ...formData, is_repeatable: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_active">Active</Label>
                <p className="text-xs text-muted-foreground">Element is available for use</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={itemToDelete?.display_name}
        itemType="element"
        isDeleting={isDeleting}
        warning={deleteWarning}
      />
    </div>
  );
}
